import { PopoverBasicWithTrigger, Tooltip } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon, PlusIcon } from "@octopusdeploy/design-system-icons";
import { selectOptionsToItems } from "@octopusdeploy/design-system-octopus-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { PackageReference, PlatformHubAccountSummary, PropertyValueResource, TemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { isValueNotSelectableInPlatformHubControlType, ControlType, isAccountControlType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import type { ReactNode } from "react";
import { useRef, useCallback } from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import type { ParameterValueRow, TemplateValueKey } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { createTemplateValueKey } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { baseCellHeightInRem, createSummaryForParameterValue, getAccounts, isAccountControlTypeWithoutDefaultValues, isOverrideVariableRow, renderSummaryNodeForParameterValue, templateParameterValueStyles, } from "~/areas/projects/components/Process/ProcessTemplates/TemplateParameterHelpers";
import DefaultValueIndicator from "~/areas/projects/components/Variables/TenantVariables/DefaultValueIndicator";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import type { ProjectSourceItems, ProjectTemplateSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { isProjectTemplateSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import PopoverWhenFocused from "~/components/PopoverWhenFocused";
import { getLabel } from "~/components/TenantVariableInput/TenantVariableInputRenderer";
import { Text } from "~/components/form/index";
import { getVariableTypeFromDisplaySettings } from "../../Variables/TenantVariables/getVariableTypeFromDisplaySettings";
export interface ParameterEditorRenderProps {
    parameter: TemplateParameterResource;
    autoFocus: boolean;
}
export interface TemplateParameterValueCellProps {
    data: ParameterValueRow;
    sourceItems: ProjectSourceItems | ProjectTemplateSourceItems;
    parameterValues: Record<string, PropertyValueResource | undefined>;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
    analyticsStepEditorDispatch: (name: string, event: StepEditorEvent) => void;
    platformHubAccounts: PlatformHubAccountSummary[];
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
    renderParameterEditor: (props: ParameterEditorRenderProps) => ReactNode;
}
export function TemplateParameterValueCell({ data, sourceItems, parameterValues, platformHubAccounts, focusedValueKey, setFocusedValueKey, renderParameterEditor }: TemplateParameterValueCellProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const valueKey = createTemplateValueKey(data.Parameter.Name, data.ValueIndex, "Value");
    const onFocus = useCallback(() => setFocusedValueKey(valueKey), [setFocusedValueKey, valueKey]);
    const onBlur = useCallback(() => setFocusedValueKey(null), [setFocusedValueKey]);
    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    const isFocused = focusedValueKey === valueKey;
    const isNonAccountControl = !isAccountControlType(controlType);
    const isAccountWithoutDefaults = isAccountControlTypeWithoutDefaultValues(data);
    const isVariableOverride = isOverrideVariableRow(data, platformHubAccounts);
    const rowCanBeFocused = isNonAccountControl || isAccountWithoutDefaults || isVariableOverride;
    const rowIsFocused = isFocused && rowCanBeFocused;
    const defaultValue = data.Value;
    const providedValue = parameterValues[data.Parameter.Name];
    const isDefaultValue = !providedValue && !!defaultValue;
    const hasValue = !!providedValue || !!defaultValue;
    const tooltipContent = isVariableOverride ? "You can only provide a single value" : "Override with a variable";
    const getValue = () => {
        if (providedValue && (!isAccountControlType(controlType) || isAccountControlTypeWithoutDefaultValues(data))) {
            return providedValue;
        }
        return defaultValue?.Value;
    };
    const renderValueContent = () => {
        if (rowIsFocused) {
            return (<div style={{ height: `${baseCellHeightInRem}rem`, width: containerRef.current ? `${containerRef.current.offsetWidth - 24}px` : space[24] }}>
                    <PopoverWhenFocused isFocused={rowIsFocused} onClickOutside={onBlur} isContentCentered={false} isFullWidth={true}>
                        <div className={styles.popoverContainer}>{renderParameterEditor({ parameter: data.Parameter, autoFocus: rowIsFocused })}</div>
                    </PopoverWhenFocused>
                </div>);
        }
        if (hasValue) {
            return renderValue();
        }
        return (<div className={styles.noValue}>
                {!data.Parameter.IsOptional && (<Tooltip content={`Value is required`}>
                        <ExclamationTriangleIcon size={24} color={themeTokens.color.icon.warning}/>
                    </Tooltip>)}
                <Text value={""} placeholder={data.Parameter.IsOptional ? "(Optional)" : "Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
            </div>);
    };
    const renderValue = () => {
        const isProjectTemplate = isProjectTemplateSourceItems(sourceItems);
        const shouldRenderAsInlineVariableValue = isAccountControlType(controlType) || controlType === ControlType.Sensitive || (isProjectTemplate && isValueNotSelectableInPlatformHubControlType(controlType));
        if (shouldRenderAsInlineVariableValue) {
            return (<>
                    <ReadonlyInlineVariableValue value={getValue()} variableType={getVariableTypeFromDisplaySettings(data.Parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={getAccounts(platformHubAccounts, sourceItems)} certificates={{}} workerPools={{}} hasEditPermissions={rowCanBeFocused} sourceItems={undefined}/>
                    <DefaultValueIndicator show={isDefaultValue && controlType === ControlType.Sensitive}/>
                </>);
        }
        const label = getLabel(data.Parameter);
        const options = selectOptionsToItems(data.Parameter.DisplaySettings["Octopus.SelectOptions"]);
        const packageParameterIsUsed = !!sourceItems.packages.items.find((p: PackageReference) => p.Properties["PackageParameterName"] === data.Parameter.Name);
        const value = parameterValues[data.Parameter.Name] || data.Value?.Value;
        const summary = createSummaryForParameterValue(label, controlType, options, sourceItems.packages.feeds, sourceItems.environments, sourceItems.workerPools.items, sourceItems.teams, sourceItems.channels, sourceItems.projects, packageParameterIsUsed)(value);
        return renderSummaryNodeForParameterValue(summary.node);
    };
    return (<div ref={containerRef}>
            {showMultipleValuesAndScopesIndicators(controlType, data) && (<div className={styles.multipleValuesAndScopesIndicator}>
                    <div>Multiple values</div>
                    <div className={styles.overrideButtonContainer}>
                        <PopoverBasicWithTrigger description={<div className={styles.popoverContainer} style={{ width: containerRef.current ? `${containerRef.current.offsetWidth - 24}px` : space[24] }}>
                                    {renderParameterEditor({ parameter: data.Parameter, autoFocus: rowIsFocused })}
                                </div>} placement="top-end" width="extra-wide" triggerElement={<IconButtonWithTooltip toolTipContent={tooltipContent} customIcon={<PlusIcon size={20} color={isVariableOverride ? themeTokens.color.icon.disabled : themeTokens.color.icon.secondary}/>} accessibleName={tooltipContent}/>} triggerAlignment="center" open={isVariableOverride ? false : undefined} contentPadding={0}/>
                    </div>
                </div>)}
            <div onFocus={onFocus} onClick={onFocus} className={styles.valueContainer}>
                {renderValueContent()}
            </div>
        </div>);
}
export const showMultipleValuesAndScopesIndicators = (controlType: ControlType, currentRow: ParameterValueRow): boolean => isAccountControlType(controlType) && currentRow.ValueIndex === 0 && currentRow.Parameter.Values.length > 0;
const styles = templateParameterValueStyles;
